import * as React from 'react'
import Page from 'components/presenter/page/Page'
import FillButton from 'components/presenter/buttons/FillButton'
import PlatButton from 'components/presenter/buttons/PlatButton'
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next'
import { useStore, newStoreId, authStoreId } from 'stores'
import { Paper, TextField } from '@material-ui/core'
import JetsoStatusChip from 'components/presenter/jetso/JetsoStatusChip'
import JetsoStatsDetail from 'components/presenter/jetso/JetsoStatsDetail'
import JetsoBanner from 'components/presenter/jetso/JetsoBanner'
import { displayDateWithTime } from 'libs/date/dateUtil'
import FormTextField from 'components/presenter/form/FormTextField'
import Autocomplete from '@material-ui/lab/Autocomplete'
import ChipInput from 'material-ui-chip-input'
import cn from 'classnames'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  DateTimePicker,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { usePageLoad } from 'libs/ui/pageLoad'
import ShopLocationListDialog from './ShopLocationListDialog'
import * as styles from './styles.module.css'

const JetsoDetail = ({location}) => {
  const { t } = useTranslation()
  const { language } = useI18next()
  const [news, setNews] = React.useState(null)
  const [isDialogOpen, setOpenDialog] = React.useState(false)
  const newsStore = useStore(newStoreId)
  const authStore = useStore(authStoreId)
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')
  const isEnd = news && news.promote_end_at ? new Date(news.promote_end_at).getTime() < Date.now() : false

  const fetchNews = async () => {
    const response = await newsStore.actions.fetchNews({
      newId: id,
    })
    if (!response.error) {
      setNews(response.get_news)
    }
  }

  const title = news ? news.title : t('mainMenu.jetsoManagement')

  const resumeNews = async () => {
    const response = await newsStore.actions.resumeNews({
      newId: id
    })
    if (!response.error) {
      setNews({
        ...news,
        is_pause: false
      })
    }
  }

  const pauseNews = async () => {
    const response = await newsStore.actions.pauseNews({
      newId: id
    })
    if (!response.error) {
      setNews({
        ...news,
        is_pause: true
      })
    }
  }

  const handleNewsAction = async () => {
    if (news.is_pause) {
      await resumeNews()
    } else {
      await pauseNews()
    }
  }

  const handlePageLoad = async () => {
    await fetchNews()
  }

  const { isPageLoaded } = usePageLoad({
    pageLoadDeps: [authStore.getters.isLoggedIn()],
    onPageLoad: handlePageLoad
  })

  const getActionStatus = () => news.is_pause ?  t('jetso.resume') : t('jetso.pause')

  const actionButton = () => (
    (!news.is_banned && news.is_published && !isEnd) && <FillButton 
      className={cn(styles.btnStatus, 'mui-button-link', !news.is_pause ? styles.pauseState : '')}
      onClick={handleNewsAction}
    >
      {getActionStatus()}
    </FillButton>
  )

  const actionButtonGroup = (showClass) => (
    <div className={showClass}>
      <FillButton
        className={cn(styles.btnStatus, styles.btnPreview, 'mui-btn-link')}>
        <a
          href={`${process.env.GATSBY_WENJETO_AUDIENCE}${language}/jetso/detail/?id=${id}`}
          target="_blank"
        >
          {t('jetso.preview')}
        </a>
      </FillButton>
      {actionButton()}
    </div>
  )
  

  const render = () => {
    if (!isPageLoaded) {
      return <></>
    }

    if (id && news) {
      const tags = news.tags ?? []
      const keywords = news.keywords ?? []
      return (
        <>
          <div className={styles.titleRoot}>
            <div className={styles.titleSection}>
              <h1 className={styles.title}>{title}</h1>
              <div className={styles.subTitleRoot}>
                <div className={styles.statusRoot}>
                  <JetsoStatusChip news={news} />
                  <p className={styles.createdTxt}>{t('jetso.createdAtFormat', {
                    date: displayDateWithTime(news.created_at)
                  })}</p>
                </div>
                {actionButtonGroup(styles.showOnPhone)}
              </div>
            </div>
            {actionButtonGroup(styles.showOnNonPhone)}
          </div>
          
          {/* <p className={styles.sectionTitle}>{t('jetso.stats')}</p> */}
          {/* <Paper className={cn(styles.paper, styles.statsRoot)} elevation={2}> */}
            {/* <JetsoStatsDetail type='views' stats={news.stats.views} /> */}
            {/* <JetsoStatsDetail type='likes' stats={news.stats.likes} /> */}
            {/* <JetsoStatsDetail type='dislikes' stats={news.stats.dislikes} /> */}
          {/* </Paper> */}

          <p className={styles.sectionTitle}>{t('jetso.leaflet')}</p>
          <Paper className={cn(styles.paper, styles.leafletRoot)} elevation={2}>
            {
              news.posters.map(poster => (
                <JetsoBanner key={poster._id} poster={poster} alt={title} />
              ))
            }
          </Paper>

          <p className={styles.sectionTitle}>{t('jetso.jetsoDetail')}</p>
          <Paper className={cn(styles.paper)} elevation={2}>
            <FormTextField
              className={cn(styles.input, 'mui-textfield-readonly')}
              fieldData={{
                value: news.title,
                isOptional: true,
              }}
              disabled
              label={t('formFields.title')}
              type="text"
            />
            <FormTextField
              className={cn(styles.input, 'mui-textarea', 'mui-textfield-readonly')}
              multiline
              fieldData={{
                value: news.description,
                isOptional: true,
              }}
              disabled
              label={t('formFields.description')}
              type="text"
            />
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.category.map(c => c.name)}
              options={news.category.map(c => c.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.category')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={keywords.map(k => k.name)}
              options={keywords.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.keyword')}
                />
              )}
            />
            <FormTextField
              className={cn(styles.input, 'mui-textarea', 'mui-textfield-readonly')}
              fieldData={{
                value: news.url,
                isOptional: true,
              }}
              disabled
              label={t('formFields.website')}
              type="text"
            />
            <ChipInput
              className={cn(styles.input, 'mui-textfield', 'mui-textfield-readonly')}
              disabled
              value={tags.map(tag => tag.name)}
              label={t('formFields.tags')}
              variant="outlined"
            />
            <KeyboardDatePicker
              label={t('formFields.promoteStartDate')}
              className={cn(styles.input, 'mui-textfield', 'mui-textfield-readonly')}
              disabled
              format="dd/MM/yyyy"
              value={news.promote_start_at}
              placeholder="DD/MM/YYYY"
              inputVariant="outlined"
              margin="dense"
            />
            <KeyboardDatePicker
              label={t('formFields.promoteEndDate')}
              className={cn(styles.input, 'mui-textfield', 'mui-textfield-readonly')}
              disabled
              format="dd/MM/yyyy"
              value={news.promote_end_at}
              placeholder="DD/MM/YYYY"
              inputVariant="outlined"
              margin="dense"
            />
          </Paper>
          <p className={styles.sectionTitle}>{t('jetso.audience')}</p>
          <Paper className={styles.paper} elevation={2}>
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.meta.care.map(k => k.name)}
              options={news.meta.care.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.care')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.meta.gender.map(k => k.name)}
              options={news.meta.gender.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.gender')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.meta.age.map(k => k.name)}
              options={news.meta.age.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.age')}
                />
              )}
            />
          </Paper>
          <p className={styles.sectionTitle}>{t('formFields.limited')}</p>
          <Paper className={styles.paper} elevation={2}>
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.meta.limited.voucher.map(k => k.name)}
              options={news.meta.limited.voucher.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.coupon')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input, 'mui-textfield-readonly')}
              disabled
              value={news.meta.limited.artist.map(k => k.name)}
              options={news.meta.limited.artist.map(k => k.name)}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.artist')}
                />
              )}
            />
          </Paper>
          <p className={styles.sectionTitle}>{t('jetso.publishArrangement')}</p>
          <Paper className={styles.paper} elevation={2}>
            <DateTimePicker
              label={t('formFields.publishTime')}
              className={cn(styles.input, 'mui-textfield', 'mui-textfield-readonly')}
              disabled
              format="dd/MM/yyyy HH:mm"
              value={news.publish_at}
              inputVariant="outlined"
              margin="dense"
            />
          </Paper>
          {
            news.locations &&
            <Paper className={cn(styles.paper, styles.shopRoot)} elevation={2}>
              <p>{t('jetso.shops')}</p>
              <PlatButton 
                className={cn(styles.btnMore)}
                onClick={() => setOpenDialog(true)}
              >
                {t('common.more')}
              </PlatButton>
            </Paper>
          }
          {
            isDialogOpen
            ? <ShopLocationListDialog
                locations={news.locations}
                onClose={() => setOpenDialog(false)}
                open
              />
            : <></>        
          }
        </>
      )
    }

    return (
      <Paper className={cn(styles.paper, styles.notFoundRoot)} elevation={2}>
        {t('jetso.newsNotFound')}
      </Paper>
    )
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Page
        className={styles.root}
        seoMeta={{
          title
        }}
        isLoading={!isPageLoaded}
      >
        {
          render()
        }
      </Page>
    </MuiPickersUtilsProvider>
  )
}

export default JetsoDetail